import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import Body from 'storybookConfig/stories/molecules/Body';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Stepper, { Step } from 'storybookConfig/stories/organisms/Stepper';
import completeIcon from '../../images/onboard-complete-icon.svg';
import topNote from '../../images/note-top.png';
import bottomNote from '../../images/note-bottom.png';
import Link from 'storybookConfig/stories/molecules/Link';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import TertiaryButton from 'storybookConfig/stories/molecules/Button/TertiaryButton';

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoteContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const NoteImage = styled.img<{ position: 'top' | 'bottom' }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  ${({ position }) => position === 'top' && 'margin-bottom: -12px;'}
  ${({ position }) => position === 'bottom' && 'margin-top: -16px;'}
`;

const Note = styled.div`
  max-width: 800px;
  padding: 32px 64px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LargeTitle = styled(Heading)`
  font-size: 48px;
  line-height: 56px;
  font-weight: 600;
  color: white;
  margin-bottom: 16px;
`;

const Content = styled(Flex)`
  text-align: center;
  max-width: 800px;
  padding: 40px;
`;

const IconContainer = styled.div`
  width: 200px;
  height: 200px;
  margin: 0 auto 20px;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const Description = styled(Body)`
  margin-bottom: 16px;
  text-align: left;
`;

const EmailLink = styled.a`
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const StyledStepper = styled(Stepper)`
  width: 100%;
  max-width: 800px;
`;

const FinishedDescription = () => {
  return (
    <Container>
      <Content column alignItemsCenter>
        <StyledStepper>
          <Step status="completed">
            <Body variant="Body/Body Small Bold" color="white">
              Step 1
            </Body>
            <Body variant="Body/Body Small" color="white">
              Contact Information
            </Body>
          </Step>
          <Step status="completed">
            <Body variant="Body/Body Small Bold" color="white">
              Step 2
            </Body>
            <Body variant="Body/Body Small" color="white">
              Brand Information
            </Body>
          </Step>
          <Step status="completed">
            <Body variant="Body/Body Small Bold" color="white">
              Step 3
            </Body>
            <Body variant="Body/Body Small" color="white">
              Brand Assets
            </Body>
          </Step>
          <Step status="completed">
            <Body variant="Body/Body Small Bold" color="white">
              Step 4
            </Body>
            <Body variant="Body/Body Small" color="white">
              Additional Info
            </Body>
          </Step>
        </StyledStepper>
        <IconContainer>
          <img src={completeIcon} alt="Complete" />
        </IconContainer>
        <LargeTitle variant="Headings/H1">
          You&apos;re In! Big Opportunities Are Just Ahead.
        </LargeTitle>
        <NoteContainer>
          <NoteImage src={topNote} alt="Top Note" position="top" />
          <Note>
            <Description>Hey!</Description>
            <Description>
              The marketplace is launching later this year—so keep an <b>eye on your inbox</b> for
              updates and next steps.
            </Description>
            <Description>
              You&apos;ll be notified as soon as it goes live, and you can always edit your
              information anytime from your <b>Settings</b> — before or after launch.
            </Description>
            <Description style={{ marginBottom: '32px' }}>
              Got questions? Reach out to us anytime at{' '}
              <EmailLink href="mailto:support@moderndropship.com">
                support@moderndropship.com
              </EmailLink>
            </Description>
            <Flex justifyContent="flex-end" gap="16px">
              <TertiaryButton as={Link} to="/settings/marketplace/brand">
                Settings Page
              </TertiaryButton>
              <PrimaryButton as={Link} to="/get-started">
                Back To Modern Dropship
              </PrimaryButton>
            </Flex>
          </Note>
          <NoteImage src={bottomNote} alt="Bottom Note" position="bottom" />
        </NoteContainer>
      </Content>
    </Container>
  );
};

export default FinishedDescription;
